/**
 * frontend/src/pages/valuation/_NormalizeAddBacks.tsx
 *
 * VAL-U2C-SCREEN-1 — the add-back list and its form. A reason is required (Save
 * stays disabled without one). The list is whatever the server returned after
 * the last call, with who added each one and when; Remove is a soft delete.
 */
import { useState } from 'react'
import type { AddBack, AddBackBody, AddBackCategory } from '@/api/valuationNormalization'
import { money } from './_NormalizeYearTable'

export const CATEGORY_LABELS: Record<AddBackCategory, string> = {
  owner_comp: 'Owner pay',
  one_time: 'One-time item',
  non_operating: 'Non-operating item',
}
const DESC_LABEL = 'Description (what this adjustment is for)'
const FIELD = 'rounded border border-gray-300 px-2 py-1 text-sm'

function when(iso?: string): string {
  const d = iso ? new Date(iso) : null
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US') : ''
}

interface Props {
  years: number[]
  addBacks: AddBack[]
  removedCount: number
  busy: boolean
  onAdd: (body: AddBackBody) => Promise<void>
  onRemove: (id: string) => Promise<void>
}

export function NormalizeAddBacks({ years, addBacks, removedCount, busy, onAdd, onRemove }: Props) {
  const latest = years.length ? years[years.length - 1] : undefined
  const [category, setCategory] = useState<AddBackCategory>('owner_comp')
  const [year, setYear] = useState<string>('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [description, setDescription] = useState('')
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [pending, setPending] = useState(false)
  const [adding, setAdding] = useState(false)

  const chosenYear = year !== '' ? Number(year) : latest
  const amountNum = Number(amount)
  const ready = reason.trim() !== '' && amount.trim() !== '' && Number.isFinite(amountNum) && chosenYear !== undefined

  async function submit() {
    if (!ready || chosenYear === undefined || adding) return
    setAdding(true)
    try {
      await onAdd({ category, year: chosenYear, amount: amountNum, reason: reason.trim(), is_pending: pending, ...(description.trim() ? { description: description.trim() } : {}) })
    } finally {
      setAdding(false)
    }
    setAmount('')
    setReason('')
    setDescription('')
    setPending(false)
  }

  async function saveDescription(a: AddBack) {
    await onAdd({
      id: a.id, category: a.category, year: a.year, amount: a.amount, reason: a.reason,
      is_pending: a.is_pending, description: (edits[a.id] ?? '').trim(),
    })
    setEdits(prev => { const { [a.id]: _gone, ...rest } = prev; return rest })
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">Add-backs</h2>
      {addBacks.length === 0 && <p className="text-sm text-gray-500">No add-backs yet.</p>}
      <ul className="space-y-2">
        {addBacks.map(a => (
          <li key={a.id} data-testid={`add-back-${a.id}`} className="rounded border border-gray-200 bg-white p-2 text-sm">
            <div className="flex items-start justify-between gap-3">
              <p>
                <span className="font-medium">{CATEGORY_LABELS[a.category] ?? a.category}</span> · FY{a.year} · {money(a.amount)}
                {a.is_pending && <span className="ml-2 text-amber-800">(pending — not counted)</span>}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRemove(a.id)}
                aria-label={`Remove ${CATEGORY_LABELS[a.category] ?? a.category} ${a.year}`}
                className="text-blue-700 underline disabled:opacity-50"
              >
                Remove
              </button>
            </div>
            <p className="text-gray-700">{a.reason}</p>
            <div className="mt-1 flex items-end gap-2">
              <label className="flex-1 text-xs text-gray-600">{DESC_LABEL}
                <input
                  type="text"
                  maxLength={300}
                  className={`${FIELD} mt-1 block w-full`}
                  value={edits[a.id] ?? a.description ?? ''}
                  onChange={e => setEdits(prev => ({ ...prev, [a.id]: e.target.value }))}
                />
              </label>
              <button
                type="button"
                disabled={busy || edits[a.id] === undefined}
                onClick={() => void saveDescription(a)}
                className="text-blue-700 underline disabled:opacity-50"
              >
                Save description
              </button>
            </div>
            <p className="text-xs text-gray-500">Added by {a.created_by ?? 'unknown'} on {when(a.created_at)}</p>
          </li>
        ))}
      </ul>
      {removedCount > 0 && <p className="text-xs text-gray-500">{removedCount} removed</p>}

      <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:grid-cols-4">
        <label className="text-xs text-gray-600">Category
          <select className={`${FIELD} mt-1 block w-full`} value={category} onChange={e => setCategory(e.target.value as AddBackCategory)}>
            {(Object.keys(CATEGORY_LABELS) as AddBackCategory[]).map(k => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600">Year
          <select className={`${FIELD} mt-1 block w-full`} value={chosenYear ?? ''} onChange={e => setYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600">Amount
          <input type="number" className={`${FIELD} mt-1 block w-full`} value={amount} onChange={e => setAmount(e.target.value)} />
        </label>
        <label className="flex items-end gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={pending} onChange={e => setPending(e.target.checked)} /> Pending (not counted yet)
        </label>
        <label className="col-span-2 text-xs text-gray-600 sm:col-span-4">{DESC_LABEL}
          <input type="text" maxLength={300} className={`${FIELD} mt-1 block w-full`} value={description} onChange={e => setDescription(e.target.value)} />
        </label>
        <label className="col-span-2 text-xs text-gray-600 sm:col-span-4">Reason
          <input type="text" className={`${FIELD} mt-1 block w-full`} value={reason} onChange={e => setReason(e.target.value)} />
        </label>
        <div className="col-span-2 sm:col-span-4">
          <button
            type="button"
            disabled={!ready || busy || adding}
            onClick={() => void submit()}
            className="rounded-md bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {adding ? 'Adding…' : 'Add add-back'}
          </button>
          {reason.trim() === '' && <span className="ml-3 text-xs text-gray-500">A reason is required for every add-back.</span>}
        </div>
      </div>
    </section>
  )
}
